import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = forwardRef<ElementRef<typeof TooltipPrimitive.Content>, ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>>(
    ({ className, sideOffset = 5, ...props }, ref) => (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                ref={ref}
                sideOffset={sideOffset}
                className={cn(
                    // MUI's zIndex.tooltip tier (1500): must clear stickyHead (1000)
                    // and friends — shadcn's stock z-50 sat below them.
                    'z-[1500] rounded-xl px-3 py-3 text-xs font-medium leading-normal',
                    'dark:bg-c7 dark:text-c11 bg-secondary-light text-info-contrast',
                    'animate-in fade-in-0 zoom-in-95',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    className,
                )}
                {...props}
            />
        </TooltipPrimitive.Portal>
    ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** Named skins replacing the old StyledComponents tooltip sx keys
 *  (stylesActionsTooltip → glass, stylesSpecialTooltip → glass-surface,
 *  tooltipLightScene/tooltipDarkScene → scene). One-off restyles go
 *  through contentClassName instead. */
const tooltipVariants = {
    default: '',
    glass: 'pointer-events-none px-2 py-1 rounded-md bg-bg-paper/50 backdrop-blur-[10px] text-text-primary dark:text-primary-contrast',
    'glass-surface': 'pointer-events-none bg-secondary-light dark:bg-bg-paper/50 backdrop-blur-[10px]',
    scene: 'px-2 py-[2px] rounded bg-c22 text-c2 dark:bg-bg-paper dark:text-info-contrast',
} as const;

type Side = 'top' | 'bottom' | 'left' | 'right';
type TooltipPlacement = Side | `${Side}-start` | `${Side}-end`;

interface TooltipProps {
    /** Tooltip content. Falsy hides the tooltip entirely (MUI-compatible). */
    title: ReactNode;
    /** Single element trigger; receives the Radix trigger props via Slot, so it must forward refs. */
    children: ReactElement;
    placement?: TooltipPlacement;
    variant?: keyof typeof tooltipVariants;
    contentClassName?: string;
    sideOffset?: number;
    arrow?: boolean;
    /** Controlled mode. Without it the tooltip opens on hover/focus. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Suppress the tooltip while keeping the child rendered (ignored in controlled mode). */
    disableHoverListener?: boolean;
    /** MUI-compatible alias for Radix disableHoverableContent: the open tooltip is not hoverable. */
    disableInteractive?: boolean;
}

/** The app-wide tooltip. MUI-shaped interface (title/placement) over the Radix
 *  compound below, so call sites migrated off @mui/material keep their props. */
function Tooltip({
    title,
    children,
    placement = 'bottom',
    variant = 'default',
    contentClassName,
    sideOffset,
    arrow,
    open,
    onOpenChange,
    disableHoverListener,
    disableInteractive,
}: TooltipProps) {
    if (!title || (open === undefined && disableHoverListener)) return children;

    const [side, align] = placement.split('-') as [Side, 'start' | 'end' | undefined];

    return (
        <TooltipProvider delayDuration={100}>
            <TooltipRoot open={open} onOpenChange={onOpenChange} disableHoverableContent={disableInteractive}>
                <TooltipTrigger asChild>{children}</TooltipTrigger>
                <TooltipContent
                    side={side}
                    align={align ?? 'center'}
                    sideOffset={sideOffset}
                    className={cn(tooltipVariants[variant], contentClassName)}
                >
                    {title}
                    {arrow && <TooltipPrimitive.Arrow className="fill-secondary-light dark:fill-c7" />}
                </TooltipContent>
            </TooltipRoot>
        </TooltipProvider>
    );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger };
